import React, { useState, useMemo } from 'react';
import { css } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import Textfield from '@atlaskit/textfield';
import Button from '@atlaskit/button';
import { Checkbox } from '@atlaskit/checkbox';
import atlassianChampionsLogo from '../../assets/images/champions/atlassian-champions-logo.png';
import { setChampionPageConsent } from '../../services/http/ChampionGraphQlOperations';
import {
  validateChampionConsentForm,
  ChampionConsentFormValues,
  ChampionConsentFormErrors,
} from '../../utils/ChampionConsentFormValidation';
import { sendUIEvent, sendTrackEvent } from '../../services/analytics';
import {
  getFormValidationFailurePayload,
  getFormSubmissionPayload,
  getCommonChampionsAttributes,
  getFormAbandonmentPayload,
  getFormFieldProgressPayload,
  calculateFormCompletion,
} from '../../analytics/champions-helpers';
import { ANALYTICS_ACTIONS } from '../../analytics/constants';
import { useEffect } from 'react';

import Logger from 'services/sentry/sentry';

/**
 * Champions Consent Form
 * 
 * Legal consents aligned with champion signup (program agreement, privacy, NDA; Success Champion when account is managed).
 * 
 * Analytics Strategy:
 * - Tracks form submission lifecycle (submitted, succeeded, failed)
 * - Logs validation failures for UX analysis
 * - Measures time to consent completion
 * - Captures journey context for funnel analysis
 */
const FORM_TYPE = 'championsConsentForm';
const PRIVACY_POLICY_URL = 'https://www.atlassian.com/legal/privacy-policy#what-this-policy-covers';
/** Same paths as champion signup `AgreementsSection` — served under `/assets/` in deploy. */
const CHAMPION_NDA_PDF_PATH = '/assets/champion-nda.pdf';
const CHAMPION_PA_PDF_PATH = '/assets/champion-pa.pdf';

type ChampionConsentFormProps = {
  accountId: string;
  /** Org-managed account (organization email); when true, Success Champion consent is shown and required. */
  accountIsManaged: boolean;
  name: string;
  emailId: string;
  userId?: string;
  entryPoint?: string;
};

const ChampionConsentForm: React.FC<ChampionConsentFormProps> = ({
  accountId,
  accountIsManaged,
  name,
  emailId,
  userId,
  entryPoint = 'directAccess',
}) => {
  const [values, setValues] = useState<ChampionConsentFormValues>({
    name,
    email: emailId,
    consentProgramAgreement: false,
    consentPrivacyPolicy: false,
    consentNda: false,
    consentSuccessChampionAgreement: false,
  });
  const [errors, setErrors] = useState<ChampionConsentFormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<
    | { type: 'success'; message: string }
    | { type: 'error'; specificError?: string }
    | null
  >(null);
  const formStartTime = useMemo(() => Date.now(), []);
  
  // Get common analytics attributes for all events
  const baseAttributes = getCommonChampionsAttributes(
    entryPoint,
    !!userId,
    userId,
    true, // Feature gate is always enabled when this form is shown
  );

  // Track form abandonment when user leaves without submitting
  useEffect(() => {
    /**
     * Handles form abandonment when user leaves the consent form without completing submission.
     * 
     * Tracks multiple abandonment scenarios:
     * - Back button click (onpopstate event)
     * - Tab close (beforeunload/pagehide events)
     * 
     * Only tracks if user has filled at least one field AND hasn't successfully submitted.
     * Checks submitStatus to prevent tracking abandonment after successful form submission.
     */
    const trackAbandonmentEvent = (reason: string) => {
      // Only track abandonment if form has been interacted with
      // (has some filled fields) AND form was not successfully submitted
      const completion = calculateFormCompletion(values);
      const hasSuccessfullySubmitted = submitStatus?.type === 'success';
      
      if (completion.filledFieldCount > 0 && !hasSuccessfullySubmitted) {
        const abandonmentPayload = getFormAbandonmentPayload(
          FORM_TYPE,
          values,
          baseAttributes,
          formStartTime,
          [],
          reason,
        );

        // Send abandonment event
        sendTrackEvent(
          abandonmentPayload.source,
          abandonmentPayload.actionSubject,
          abandonmentPayload.action,
          abandonmentPayload.attributes,
          userId,
        );
      }
    };

    // Handle back button navigation
    const handlePopState = () => {
      trackAbandonmentEvent('backButton');
    };

    // Handle page/tab close and navigation away
    const handleBeforeUnload = () => {
      trackAbandonmentEvent('pageUnload');
    };

    // Attach listeners for different abandonment scenarios
    window.addEventListener('popstate', handlePopState);
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Cleanup listeners on unmount
    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [values, submitStatus, baseAttributes, userId]);

  const baseUrl =
    process.env['REACT_APP_ENVI'] === 'local' ? 'http://localhost:9000' : process.env['HOME_BASE_URL'];

  const updateField = <K extends keyof ChampionConsentFormValues>(
    key: K,
    value: ChampionConsentFormValues[K]
  ) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // UI Event: Submit button clicked
    sendUIEvent(
      FORM_TYPE,
      'button',
      ANALYTICS_ACTIONS.CLICKED,
      'championsConsentSubmit',
      {
        ...baseAttributes,
        formType: FORM_TYPE,
      },
      userId,
    );

    const validationErrors = validateChampionConsentForm(values, { accountIsManaged });
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      // Track validation failure with helper
      const validationPayload = getFormValidationFailurePayload(
        FORM_TYPE,
        Object.keys(validationErrors),
        baseAttributes,
        Date.now() - formStartTime,
      );
      sendTrackEvent(
        validationPayload.source,
        validationPayload.actionSubject,
        validationPayload.action,
        validationPayload.attributes,
        userId,
      );
      Logger.warn(`[ChampionConsentForm] Validation failed aaid=${accountId}`);
      return;
    }

    Logger.info(`[ChampionConsentForm] Submitting consent aaid=${accountId}`);

    setIsSubmitting(true);
    setSubmitStatus(null);

    // Track form field progress on submission
    const progressPayload = getFormFieldProgressPayload(
      FORM_TYPE,
      values,
      baseAttributes,
      formStartTime,
    );
    sendTrackEvent(
      progressPayload.source,
      progressPayload.actionSubject,
      progressPayload.action,
      progressPayload.attributes,
      userId,
    );

    // Track form submission with timing
    const submittedPayload = getFormSubmissionPayload(
      'submitted',
      FORM_TYPE,
      baseAttributes,
      Date.now() - formStartTime,
    );
    sendTrackEvent(
      submittedPayload.source,
      submittedPayload.actionSubject,
      submittedPayload.action,
      submittedPayload.attributes,
      userId,
    );

    const baseUrl =
      process.env['REACT_APP_ENVI'] === 'local' ? 'http://localhost:9000' : process.env['HOME_BASE_URL'];
    try {
      const result = await setChampionPageConsent(
        {
          aaid: accountId,
          consentProgramAgreement: values.consentProgramAgreement,
          consentPrivacyPolicy: values.consentPrivacyPolicy,
          consentNda: values.consentNda,
          ...(accountIsManaged
            ? { consentSuccessChampionAgreement: values.consentSuccessChampionAgreement }
            : {}),
        },
        baseUrl,
        baseUrl,
      );
      if (result?.success) {
        // Track successful submission
        const successPayload = getFormSubmissionPayload(
          'succeeded',
          FORM_TYPE,
          baseAttributes,
          Date.now() - formStartTime,
        );
        sendTrackEvent(
          successPayload.source,
          successPayload.actionSubject,
          successPayload.action,
          successPayload.attributes,
          userId,
        );
        Logger.info(`[ChampionConsentForm] Consent submission successful aaid=${accountId}`);
        setSubmitStatus({
          type: 'success',
          message: 'Consent submitted successfully.',
        });
        setErrors({});
      } else {
        // Track API error
        const exceptionPayload = getFormSubmissionPayload(
          'failed',
          FORM_TYPE,
          baseAttributes,
          Date.now() - formStartTime,
          result?.message ?? 'Unknown error',
        );
        sendTrackEvent(
          exceptionPayload.source,
          exceptionPayload.actionSubject,
          exceptionPayload.action,
          exceptionPayload.attributes,
          userId,
        );
        Logger.error(new Error('[ChampionConsentForm] Consent submission failed'), {
          extra: {
            aaid: accountId,
            message: result?.message,
            result,
          },
        });
        setSubmitStatus({
          type: 'error',
          specificError: result?.message ?? undefined,
        });
      }
    } catch (error) {
      const err =
        error instanceof Error
          ? error
          : new Error('[ChampionConsentForm] Unexpected error during consent submission');
      Logger.error(err, {
        extra: {
          aaid: accountId,
          ...(error instanceof Error ? {} : { thrown: error }),
        },
      });

      // Track exception
      const exceptionPayload = getFormSubmissionPayload(
        'failed',
        FORM_TYPE,
        baseAttributes,
        Date.now() - formStartTime,
        err.message,
      );
      sendTrackEvent(
        exceptionPayload.source,
        exceptionPayload.actionSubject,
        exceptionPayload.action,
        exceptionPayload.attributes,
        userId,
      );

      setSubmitStatus({
        type: 'error',
        specificError: err.message,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitStatus?.type === 'success') {
    return (
      <div css={successPageStyles}>
        <div css={successMessageContainerStyles}>
          <p css={successMessageTextStyles}>{submitStatus.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="champion-form champion-consent-form" css={outerStyles}>
      <div css={formColumnStyles}>
        <div css={logoBandStyles}>
          <img src={atlassianChampionsLogo} alt="Atlassian Champions" css={logoStyles} />
        </div>

        <div css={titleBandStyles}>
          <h1 css={consentPageTitleStyles}>Confirm your Champion account and program consent</h1>
        </div>

        <div css={formShellStyles}>
          <form css={formStyles} onSubmit={handleSubmit}>
            <div css={ssoIntroBlockStyles}>
              <p css={ssoIntroTextStyles}>
                We&rsquo;re updating Community Platform SSO (Single Sign-On) to align with Atlassian identity standards,
                and introducing a new Champions Program Agreement. Complete this form to ensure you have seamless access
                to Champion resources, an accurate profile, and that we contact you at the right email address.
              </p>
            </div>

            <div css={helpIntroBlockStyles}>
              <p css={helpIntroLineStyles}>
                Questions marked with an{' '}
                <abbr css={consentIntroAsteriskStyles} title="required" aria-hidden="true">
                  *
                </abbr>{' '}
                are required.
              </p>
              <p css={helpIntroLineStyles}>Issues with the form? We&rsquo;re here to help.</p>
              <p css={helpIntroLineStyles}>
                Email us at{' '}
                <a href="mailto:communitymanagers@atlassian.com" css={helpIntroEmailLinkStyles}>
                  communitymanagers@atlassian.com
                </a>
              </p>
            </div>

            <div css={consentBannerStyles} className="champion-form-consent-message">
              <span css={consentBannerIconStyles} aria-hidden="true">
                <svg viewBox="0 0 16 16" width="14" height="14" fill="none" focusable="false">
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M8 0C12.4183 0 16 3.58172 16 8C16 12.4183 12.4183 16 8 16C3.58172 16 0 12.4183 0 8C0 3.58172 3.58172 0 8 0ZM6.5 6.75V8.25H7.25V12.5H8.75V7.5C8.75 7.08579 8.41421 6.75 8 6.75H6.5ZM8 3.5C7.44772 3.5 7 3.94772 7 4.5C7 5.05228 7.44772 5.5 8 5.5C8.55228 5.5 9 5.05228 9 4.5C9 3.94772 8.55228 3.5 8 3.5Z"
                    fill="#AF59E1"
                  />
                </svg>
              </span>
              <span css={consentBannerTextStyles}>
                Check you&rsquo;re logged in with the account used on your champions application.
              </span>
            </div>

            <div>
              <div css={consentFieldBlockStyles}>
                <label css={consentFieldLabelStyles} htmlFor="consent-name">
                  Name
                </label>
                <div css={consentTextFieldShellStyles} className="consent-form-text-field-wrap">
                  <Textfield
                    id="consent-name"
                    value={values.name}
                    isDisabled
                    isCompact
                  />
                </div>
              </div>

              <div css={consentEmailFieldBlockStyles}>
                <label css={consentFieldLabelStyles} htmlFor="consent-email">
                  Contact email
                </label>
                <div css={consentTextFieldShellStyles} className="consent-form-text-field-wrap">
                  <Textfield
                    id="consent-email"
                    type="email"
                    value={values.email}
                    isDisabled
                    isCompact
                  />
                </div>
              </div>
            </div>

            <div css={consentAgreementsSectionStyles}>
              <div css={consentAgreementRowsStackStyles}>
              <div
                className={
                  errors?.consentProgramAgreement
                    ? 'consent-agreement-checkbox consent-agreement-checkbox--multiline consent-agreement-checkbox--multiline-compact consent-agreement-checkbox-privacy--error'
                    : 'consent-agreement-checkbox consent-agreement-checkbox--multiline consent-agreement-checkbox--multiline-compact'
                }
              >
                <Checkbox
                  name="consent_program_agreement"
                  label={
                    <>
                      By checking this box, I acknowledge that I have read, understood, and agree to abide by the{' '}
                      <a
                        href={`${baseUrl}${CHAMPION_PA_PDF_PATH}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        css={consentProgramAgreementLinkStyles}
                      >
                        Atlassian Champions Program Agreement
                      </a>
                      {" and to uphold Atlassian's values in all my activities as a Champion."}<span className="consent-agreement-asterisk" css={consentAgreementAsteriskStyles} aria-hidden="true">
                        *
                      </span>
                    </>
                  }
                  isChecked={values.consentProgramAgreement}
                  isInvalid={!!errors.consentProgramAgreement}
                  onChange={() => updateField('consentProgramAgreement', !values.consentProgramAgreement)}
                />
              </div>

              <div
                className={
                  errors?.consentPrivacyPolicy
                    ? 'consent-agreement-checkbox consent-agreement-checkbox--multiline consent-agreement-checkbox--multiline-compact consent-agreement-checkbox-privacy--error'
                    : 'consent-agreement-checkbox consent-agreement-checkbox--multiline consent-agreement-checkbox--multiline-compact'
                }
              >
                <Checkbox
                  name="consent_privacy_policy"
                  label={
                    <>
                      I acknowledge the{' '}
                      <a
                        href={PRIVACY_POLICY_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        css={consentProgramAgreementLinkStyles}
                      >
                        Atlassian Privacy Policy
                      </a>{'.'}<span className="consent-agreement-asterisk" css={consentAgreementAsteriskStyles} aria-hidden="true">
                        *
                      </span>
                    </>
                  }
                  isChecked={values.consentPrivacyPolicy}
                  isInvalid={!!errors.consentPrivacyPolicy}
                  onChange={() => updateField('consentPrivacyPolicy', !values.consentPrivacyPolicy)}
                />
              </div>

              <div
                className={
                  errors?.consentNda
                    ? 'consent-agreement-checkbox consent-agreement-checkbox--multiline consent-agreement-checkbox--multiline-compact consent-agreement-checkbox-nda--error'
                    : 'consent-agreement-checkbox consent-agreement-checkbox--multiline consent-agreement-checkbox--multiline-compact'
                }
              >
                <Checkbox
                  name="consent_nda"
                  label={
                    <>
                      By checking this box, I agree to the{' '}
                      <a
                        href={`${baseUrl}${CHAMPION_NDA_PDF_PATH}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        css={consentProgramAgreementLinkStyles}
                      >
                        Non-Disclosure Agreement
                      </a>{'.'}<span className="consent-agreement-asterisk" css={consentAgreementAsteriskStyles} aria-hidden="true">
                        *
                      </span>
                    </>
                  }
                  isChecked={values.consentNda}
                  isInvalid={!!errors.consentNda}
                  onChange={() => updateField('consentNda', !values.consentNda)}
                />
              </div>

              {accountIsManaged && (
                <div
                  className={
                    errors?.consentSuccessChampionAgreement
                      ? 'consent-agreement-checkbox consent-agreement-checkbox--multiline consent-agreement-checkbox--multiline-compact consent-agreement-checkbox-nda--error'
                      : 'consent-agreement-checkbox consent-agreement-checkbox--multiline consent-agreement-checkbox--multiline-compact'
                  }
                >
                  <Checkbox
                    name="consent_success_champion_agreement"
                    label={
                      <>
                        Atlassian may use the information provided by me, together with my profile and activity on the
                        Atlassian platform, to confirm my affiliation with an active Atlassian account and support me as a
                        Success Champion.<span className="consent-agreement-asterisk" css={consentAgreementAsteriskStyles} aria-hidden="true">
                          *
                        </span>
                      </>
                    }
                    isChecked={values.consentSuccessChampionAgreement}
                    isInvalid={!!errors.consentSuccessChampionAgreement}
                    onChange={() =>
                      updateField(
                        'consentSuccessChampionAgreement',
                        !values.consentSuccessChampionAgreement,
                      )
                    }
                  />
                </div>
              )}
              </div>

              {(errors?.consentProgramAgreement ||
                errors?.consentPrivacyPolicy ||
                errors?.consentNda ||
                errors?.consentSuccessChampionAgreement) && (
                <div css={errorStyles}>
                  {errors.consentProgramAgreement ||
                    errors.consentPrivacyPolicy ||
                    errors.consentNda ||
                    errors.consentSuccessChampionAgreement}
                </div>
              )}
            </div>

            {submitStatus?.type === 'error' && (
              <div css={submitErrorBannerStyles}>
                <div>Oops! Something went wrong.</div>
                {submitStatus.specificError && (
                  <div css={submitErrorBannerDetailStyles}>{submitStatus.specificError}</div>
                )}
              </div>
            )}

            <div css={consentSubmitWrapStyles} className="champion-form-submit">
              <Button
                type="submit"
                appearance="primary"
                isDisabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting\u2026' : 'Submit'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ChampionConsentForm;

const outerStyles = css({
  display: 'flex',
  justifyContent: 'center',
  position: 'relative',
  zIndex: 0,
  overflow: 'visible',
  padding: token('space.600'),
  paddingTop: token('space.075'),
  /* Name / Contact email: 320×40, padding 8×12, inner text 16/24 */
  '& .consent-form-text-field-wrap [data-ds--text-field--container]': {
    width: '320px',
    height: '40px',
    minHeight: '40px',
    maxHeight: '40px',
    padding: '8px 12px',
    borderRadius: '0.25rem',
    borderColor: '#c9c9c9',
    backgroundColor: '#fff',
    boxSizing: 'border-box',
  },
  '& .consent-form-text-field-wrap [data-ds--text-field--container] input': {
    fontFamily: token('font.family.body'),
    fontSize: '16px',
    lineHeight: '24px',
    fontWeight: 400,
    padding: 0,
    height: '24px',
    minHeight: '24px',
  },
  '& .consent-form-text-field-wrap [data-ds--text-field--container] input:not(:disabled)': {
    color: '#080F21',
    WebkitTextFillColor: '#080F21',
  },
  '& .consent-form-text-field-wrap [data-ds--text-field--container] input:disabled': {
    color: 'rgba(8, 15, 33, 0.29)' /* #080F21 @ 29% */,
    WebkitTextFillColor: 'rgba(8, 15, 33, 0.29)',
    opacity: 1,
  },
});

const formColumnStyles = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: '100%',
});

/** Logo band: 14px vertical padding, 94px horizontal; 8px below before title */
const logoBandStyles = css({
  boxSizing: 'border-box',
  padding: '14px 94px',
  marginBottom: '8px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
});

const logoStyles = css({
  width: '179px',
  height: '16px',
  objectFit: 'contain',
  display: 'block',
});

/** Title band: 940×128, 28px below before form shell */
const titleBandStyles = css({
  boxSizing: 'border-box',
  width: '940px',
  height: '128px',
  marginBottom: '28px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
});

const consentPageTitleStyles = css({
  margin: 0,
  fontFamily: token('font.family.heading'),
  fontSize: '56px',
  lineHeight: '64px',
  fontWeight: 700,
  fontStyle: 'normal',
  color: '#000',
});

/** Form shell: 1027×959 (min-height so content can grow) */
const formShellStyles = css({
  boxSizing: 'border-box',
  width: '1027px',
  minHeight: '959px',
});

const formStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: token('space.0'),
});

/** SSO intro: 1027×96, body 20/32, left-aligned */
const ssoIntroBlockStyles = css({
  boxSizing: 'border-box',
  width: '100%',
  minHeight: '96px',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'flex-start',
  textAlign: 'left',
  marginBottom: '24px',
});

const ssoIntroTextStyles = css({
  margin: 0,
  maxWidth: '100%',
  fontFamily: token('font.family.body'),
  fontSize: '20px',
  lineHeight: '32px',
  fontWeight: 400,
  fontStyle: 'normal',
  color: '#172B4D',
});

/** Help lines: 1027×96, body 20/32, left-aligned */
const helpIntroBlockStyles = css({
  boxSizing: 'border-box',
  width: '100%',
  minHeight: '96px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'flex-start',
  textAlign: 'left',
  marginBottom: 0,
});

const helpIntroLineStyles = css({
  margin: 0,
  fontFamily: token('font.family.body'),
  fontSize: '20px',
  lineHeight: '32px',
  fontWeight: 400,
  fontStyle: 'normal',
  color: '#000',
  '& + &': {
    marginTop: 0,
  },
});

const consentIntroAsteriskStyles = css({
  color: '#C9372C',
  textDecoration: 'none',
  fontWeight: 400,
});

const helpIntroEmailLinkStyles = css({
  color: '#000',
  textDecoration: 'underline',
});

/** 63px below help intro; 995px wide; text 14/20 #292A2E; 40px below before Name */
const consentBannerStyles = css({
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  width: '995px',
  maxWidth: '100%',
  minHeight: '20px',
  marginTop: '63px',
  marginBottom: '40px',
});

const consentBannerIconStyles = css({
  display: 'inline-flex',
  flexShrink: 0,
  alignItems: 'center',
  lineHeight: '20px',
  '& svg': {
    width: '14px',
    height: '14px',
    display: 'block',
  },
});

const consentBannerTextStyles = css({
  fontFamily: token('font.family.body'),
  fontSize: '14px',
  lineHeight: '20px',
  fontWeight: 400,
  fontStyle: 'normal',
  color: '#292A2E',
});

/**
 * Space above agreement block (after name/email). Submit spacing uses consentSubmitWrapStyles marginTop 63px.
 */
const consentAgreementsSectionStyles = css({
  marginTop: '63px',
  marginBottom: 0,
});

/** 11px between each agreement row (design); flex gap avoids margin-collapse with multiline min-heights. */
const consentAgreementRowsStackStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: '11px',
});

/** Name field: directly under banner (banner already has 40px margin-bottom) */
const consentFieldBlockStyles = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
});

/** Contact email: 40px below Name */
const consentEmailFieldBlockStyles = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  marginTop: '40px',
});

/** Label: body 14/20, #44546F; 4px gap to text box */
const consentFieldLabelStyles = css({
  display: 'inline-block',
  width: 'fit-content',
  minHeight: '20px',
  lineHeight: '20px',
  fontFamily: token('font.family.body'),
  fontSize: '14px',
  fontWeight: 400,
  fontStyle: 'normal',
  color: '#44546F',
  marginBottom: '4px',
});

/** Text field outer shell: 320px fill */
const consentTextFieldShellStyles = css({
  width: '320px',
  maxWidth: '100%',
});

const consentAgreementAsteriskStyles = css({
  color: '#AE2E24 !important',
  WebkitTextFillColor: '#AE2E24',
});

const consentProgramAgreementLinkStyles = css({
  color: '#1868DB',
  textDecoration: 'none',
});

/** Submit sits 63px below the agreements block (last checkbox row when no API error banner above it). */
const consentSubmitWrapStyles = css({
  marginTop: '63px',
});

const errorStyles = css({
  color: 'var(--champion-form-error)',
  fontFamily: 'sans-serif',
  fontSize: '0.75rem',
  fontWeight: 400,
  marginTop: token('space.050'),
});

/** Full-page success — matches ChampionApplicationForm after signup */
const successPageStyles = css({
  display: 'flex',
  width: '100%',
  minHeight: '400px',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: token('space.600'),
});

const successMessageContainerStyles = css({
  maxWidth: '600px',
});

const successMessageTextStyles = css({
  color: token('color.text'),
  fontFamily: 'sans-serif',
  fontWeight: 400,
  fontSize: '18px',
  lineHeight: '24px',
  textAlign: 'center',
});

/** Submit error banner — matches ChampionApplicationForm; vertical margin so it doesn’t touch submit */
const submitErrorBannerStyles = css({
  marginTop: token('space.200'),
  marginBottom: token('space.200'),
  padding: token('space.200'),
  borderRadius: '0.25rem',
  backgroundColor: '#fef1ee',
  color: 'var(--champion-form-error)',
  fontWeight: 400,
});

const submitErrorBannerDetailStyles = css({
  marginTop: token('space.050'),
  fontSize: '0.875rem',
});
